import { PRINT_OPTIONS_CHOICE_CUSTOMER, PRINT_OPTIONS_CHOICE_DESIGNER } from './types';
import type { PrintOptionSelectionDetail, PrintOptionsChoice } from './types';

/**
 * The machine-readable half of the reply, mapped by the backend onto brief document fields.
 *
 * `productOptions` is a JSON-encoded string rather than an object. That is not a mistake and not
 * ours to fix: the pilot ships it this way, the prompt reads it this way, and sessions already
 * persisted in production contain it. Changing the encoding would break both the backend mapping
 * and the rehydration of every existing conversation.
 */
export type PrintOptionsValue = {
    productOptionsDecision: string;
    productOptions?: string;
};

/**
 * A submitted print-options answer, as it appears on the wire and in persisted history.
 *
 * Carries the same data twice, deliberately: `value` is what the backend consumes, while
 * `selections` and `selectionDetails` exist so the answered bubble can re-render without
 * refetching the configurator.
 */
export type PrintOptionsReply = {
    choice: PrintOptionsChoice;
    value?: PrintOptionsValue;
    /** Attribute-key to value-key pairs. CUSTOMER only. */
    selections?: Record<string, string>;
    /** Display names resolved at submit time. CUSTOMER only. */
    selectionDetails?: PrintOptionSelectionDetail[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringRecord(value: unknown): value is Record<string, string> {
    return isRecord(value) && Object.values(value).every((entry) => typeof entry === 'string');
}

function parseValue(input: unknown): PrintOptionsValue | null {
    if (!isRecord(input)) {
        return null;
    }

    const { productOptionsDecision, productOptions } = input;

    if (typeof productOptionsDecision !== 'string') {
        return null;
    }
    if (productOptions !== undefined && typeof productOptions !== 'string') {
        return null;
    }

    return {
        productOptionsDecision,
        ...(productOptions !== undefined ? { productOptions } : {}),
    };
}

function parseSelectionDetail(input: unknown): PrintOptionSelectionDetail | null {
    if (!isRecord(input)) {
        return null;
    }

    const { optionName, valueName, imageUrl } = input;

    if (typeof optionName !== 'string' || optionName === '') {
        return null;
    }
    if (typeof valueName !== 'string') {
        return null;
    }
    if (imageUrl !== undefined && typeof imageUrl !== 'string') {
        return null;
    }

    return {
        optionName,
        valueName,
        ...(imageUrl !== undefined ? { imageUrl } : {}),
    };
}

/**
 * Parses a submitted print-options reply from wire content or persisted history.
 *
 * Only `choice` is required, which is what makes older rows renderable. Three shapes exist in
 * production and all three must parse:
 *
 * 1. DESIGNER — `choice` alone, no selections.
 * 2. CUSTOMER with `selectionDetails` — the current shape, display names included.
 * 3. CUSTOMER with `selections` but no `selectionDetails` — written before details were added.
 *    The message view derives rows from the raw keys for these, so they still render, just with
 *    attribute keys instead of localised names.
 *
 * `value` is optional for the same reason: the answered bubble never reads it, so a row missing
 * it should still render rather than fail the parse over a field only the backend consumes.
 *
 * @param data - The parsed `data` of a user message's wire envelope
 * @returns A validated `PrintOptionsReply`, or `null` if validation fails
 */
export function parsePrintOptionsReply(data: unknown): PrintOptionsReply | null {
    if (!isRecord(data)) {
        return null;
    }

    const { choice, value, selections, selectionDetails } = data;

    if (choice !== PRINT_OPTIONS_CHOICE_CUSTOMER && choice !== PRINT_OPTIONS_CHOICE_DESIGNER) {
        return null;
    }

    let parsedValue: PrintOptionsValue | undefined;
    if (value !== undefined) {
        const candidate = parseValue(value);
        if (!candidate) {
            return null;
        }
        parsedValue = candidate;
    }

    if (selections !== undefined && !isStringRecord(selections)) {
        return null;
    }

    let parsedDetails: PrintOptionSelectionDetail[] | undefined;
    if (selectionDetails !== undefined) {
        if (!Array.isArray(selectionDetails)) {
            return null;
        }
        parsedDetails = [];
        for (const item of selectionDetails) {
            const detail = parseSelectionDetail(item);
            if (!detail) {
                return null;
            }
            parsedDetails.push(detail);
        }
    }

    return {
        choice,
        ...(parsedValue !== undefined ? { value: parsedValue } : {}),
        ...(selections !== undefined ? { selections: { ...selections } } : {}),
        ...(parsedDetails !== undefined ? { selectionDetails: parsedDetails } : {}),
    };
}
